// src/pages/TicketDeliveryManagement/utils/printDeliverySummary.ts
// In / xuất PDF phiếu giao vé tóm tắt.
//
// Vì sao KHÔNG dùng thư viện (jsPDF/pdfmake...): project hiện chưa có bất kỳ dependency sinh PDF
// phía client nào (đã kiểm tra package.json), trong khi nội dung cần in chỉ là 1 bảng tĩnh đơn
// giản. Dùng hộp thoại in sẵn có của trình duyệt ("Destination: Save as PDF") vừa cho ra file PDF
// đúng yêu cầu, vừa không phải thêm ~300KB bundle mới.
//
// Vì sao dùng iframe ẩn thay vì `@media print` trên chính trang: `window.print()` trên trang hiện
// tại buộc phải viết CSS ẩn toàn bộ layout admin (sidebar/header/table...), rất dễ vỡ và ảnh hưởng
// các trang khác. Iframe ẩn có document riêng — chỉ chứa đúng nội dung phiếu, không đụng tới CSS
// toàn cục.

import moment from 'moment';
import { TicketDelivery, TicketDeliveryLine } from '../types';
import { createScreenText } from 'helpers/screenText';
const st = createScreenText('ticket_delivery_management');

const escapeHtml = (value: unknown): string =>
  String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');

const formatNumber = (value?: number | null): string =>
  typeof value === 'number' ? value.toLocaleString('vi-VN') : '0';

// Số lượng 1 dòng: ưu tiên giá trị BE trả về, fallback tính từ dải số
const lineQuantity = (line: TicketDeliveryLine): number => {
  if (typeof line.quantity === 'number') return line.quantity;
  const start = Number(line.rangeStart);
  const end = Number(line.rangeEnd);
  if (Number.isNaN(start) || Number.isNaN(end) || end < start) return 0;
  return end - start + 1;
};

// Mã/tên đại lý phải truyền từ ngoài vào: TicketDeliveryDto chỉ có `agentId`
export interface PrintAgentInfo {
  code?: string | null;
  name?: string | null;
}

const buildHtml = (delivery: TicketDelivery, agent: PrintAgentInfo): string => {
  const lines = delivery.lines || [];
  const totalQuantity =
    typeof delivery.totalQuantity === 'number'
      ? delivery.totalQuantity
      : lines.reduce((sum, line) => sum + lineQuantity(line), 0);

  const rows = lines
    .map(
      (line, index) => `
        <tr>
          <td class="text-center">${index + 1}</td>
          <td>${escapeHtml(line.symbol)}</td>
          <td>${escapeHtml(line.rangeStart)}</td>
          <td>${escapeHtml(line.rangeEnd)}</td>
          <td class="text-end">${formatNumber(lineQuantity(line))}</td>
        </tr>`,
    )
    .join('');

  const emptyRow = st('tr_td_colspan_5_class_text_text');

  return st('doctype_html_html_lang_vi_head_text', { value: escapeHtml(delivery.deliveryCode), value_2: escapeHtml(delivery.deliveryCode), value_3: escapeHtml(agent.code || '-'), value_4: escapeHtml(agent.name || '-'), value_5: escapeHtml(delivery.planCode || delivery.issuancePlanId || '-'), value_6: escapeHtml(delivery.statusName || '-'), value_7: escapeHtml(delivery.deliveredByName || delivery.createdByName || '-'), value_8: escapeHtml(
          delivery.deliveredAt || delivery.createdAt
            ? moment(delivery.deliveredAt || delivery.createdAt).format('DD/MM/YYYY HH:mm')
            : moment().format('DD/MM/YYYY HH:mm'),
        ), value_9: escapeHtml(delivery.note || '-'), value_10: rows || emptyRow, value_11: formatNumber(totalQuantity) });
};

/**
 * Mở hộp thoại in của trình duyệt với nội dung phiếu giao vé — người dùng chọn "Save as PDF"
 * để tải file PDF về. Không phụ thuộc thư viện ngoài.
 */
export const printDeliverySummary = (delivery: TicketDelivery, agent: PrintAgentInfo): void => {
  const iframe = document.createElement('iframe');
  iframe.setAttribute('aria-hidden', 'true');
  iframe.style.position = 'fixed';
  iframe.style.right = '0';
  iframe.style.bottom = '0';
  iframe.style.width = '0';
  iframe.style.height = '0';
  iframe.style.border = '0';
  document.body.appendChild(iframe);

  const frameWindow = iframe.contentWindow;
  const frameDocument = frameWindow?.document;
  if (!frameWindow || !frameDocument) {
    iframe.remove();
    return;
  }

  frameDocument.open();
  frameDocument.write(buildHtml(delivery, agent));
  frameDocument.close();

  // Đợi document trong iframe render xong rồi mới gọi print (Safari/Firefox cần nhịp này)
  const triggerPrint = () => {
    try {
      frameWindow.focus();
      frameWindow.print();
    } finally {
      // Giữ iframe đủ lâu để hộp thoại in đọc xong nội dung rồi mới gỡ khỏi DOM
      setTimeout(() => iframe.remove(), 1000);
    }
  };

  if (frameDocument.readyState === 'complete') {
    setTimeout(triggerPrint, 50);
  } else {
    iframe.onload = triggerPrint;
  }
};

/**
 * Ghép dữ liệu phiếu để in ngay sau khi tạo, phòng trường hợp API tạo phiếu chỉ trả về id/mã
 * phiếu mà không kèm lines/thông tin đại lý — khi đó lấy bù từ dữ liệu người dùng vừa nhập.
 */
export const buildDeliveryForPrint = (
  responsePayload: any,
  fallback: Partial<TicketDelivery>,
): TicketDelivery => {
  const payload = (responsePayload && typeof responsePayload === 'object' ? responsePayload : {}) as
    Partial<TicketDelivery>;

  return {
    id: payload.id ?? fallback.id ?? 0,
    deliveryCode: payload.deliveryCode || fallback.deliveryCode || st('chua_co_ma_text'),
    issuancePlanId: payload.issuancePlanId ?? fallback.issuancePlanId ?? 0,
    planCode: payload.planCode || fallback.planCode,
    agentId: payload.agentId ?? fallback.agentId ?? 0,
    status: payload.status ?? fallback.status ?? 1,
    statusName: payload.statusName || fallback.statusName,
    note: payload.note || fallback.note,
    totalQuantity: payload.totalQuantity ?? fallback.totalQuantity,
    deliveredByName: payload.deliveredByName || fallback.deliveredByName,
    deliveredAt: payload.deliveredAt || fallback.deliveredAt,
    createdAt: payload.createdAt || fallback.createdAt,
    createdByName: payload.createdByName || fallback.createdByName,
    lines: payload.lines && payload.lines.length > 0 ? payload.lines : fallback.lines || [],
  };
};
